import React, { useState, useRef, useCallback, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
    /** Content to show in the tooltip (string or React node) */
    content: React.ReactNode;
    /** Placement relative to the trigger */
    placement?: TooltipPlacement;
    /** Delay (ms) before showing the tooltip */
    delay?: number;
    /** Delay (ms) before hiding the tooltip */
    hideDelay?: number;
    /** Max width of the tooltip */
    maxWidth?: number | string;
    /** Whether the tooltip is disabled */
    disabled?: boolean;
    /** Additional class for the tooltip content */
    className?: string;
    /** Additional class for the trigger wrapper */
    triggerClassName?: string;
    /**
     * When false, the tooltip does not open on focus (keyboard / click focus), only on hover.
     * Use for icon buttons that open modals so focus doesn't leave the tip stuck on screen.
     */
    showOnFocus?: boolean;
    /** Hide the tooltip on pointer down (before click). Stops the tip lingering when opening dialogs. */
    dismissOnPointerDown?: boolean;
    children: React.ReactNode;
}

const OFFSET = 8;

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    placement = "top",
    delay = 200,
    hideDelay = 0,
    maxWidth = 240,
    disabled = false,
    className,
    triggerClassName,
    showOnFocus = true,
    dismissOnPointerDown = false,
    children,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const tooltipId = useId();
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearShowTimeout = useCallback(() => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
    }, []);

    const clearHideTimeout = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewport = { w: window.innerWidth, h: window.innerHeight };
        const padding = 8;

        let top = 0;
        let left = 0;
        let effective = placement;

        const fits = {
            top: triggerRect.top >= tooltipRect.height + OFFSET + padding,
            bottom: triggerRect.bottom + tooltipRect.height + OFFSET + padding <= viewport.h,
            left: triggerRect.left >= tooltipRect.width + OFFSET + padding,
            right: triggerRect.right + tooltipRect.width + OFFSET + padding <= viewport.w,
        };

        // Auto-flip if placement doesn't fit
        if (placement === "top" && !fits.top && fits.bottom) effective = "bottom";
        else if (placement === "bottom" && !fits.bottom && fits.top) effective = "top";
        else if (placement === "left" && !fits.left && fits.right) effective = "right";
        else if (placement === "right" && !fits.right && fits.left) effective = "left";

        const triggerCenterX = triggerRect.left + triggerRect.width / 2;
        const triggerCenterY = triggerRect.top + triggerRect.height / 2;

        switch (effective) {
            case "top":
                top = triggerRect.top - tooltipRect.height - OFFSET;
                left = triggerCenterX - tooltipRect.width / 2;
                break;
            case "bottom":
                top = triggerRect.bottom + OFFSET;
                left = triggerCenterX - tooltipRect.width / 2;
                break;
            case "left":
                top = triggerCenterY - tooltipRect.height / 2;
                left = triggerRect.left - tooltipRect.width - OFFSET;
                break;
            case "right":
                top = triggerCenterY - tooltipRect.height / 2;
                left = triggerRect.right + OFFSET;
                break;
        }

        // Clamp to viewport
        left = Math.max(padding, Math.min(viewport.w - tooltipRect.width - padding, left));
        top = Math.max(padding, Math.min(viewport.h - tooltipRect.height - padding, top));

        setCoords({ top, left });
    }, [placement]);

    const handleShow = useCallback(() => {
        clearHideTimeout();
        if (disabled) return;
        showTimeoutRef.current = setTimeout(() => {
            setIsVisible(true);
            showTimeoutRef.current = null;
        }, delay);
    }, [disabled, delay, clearHideTimeout]);

    const handleHide = useCallback(() => {
        clearShowTimeout();
        hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            hideTimeoutRef.current = null;
        }, hideDelay);
    }, [hideDelay, clearShowTimeout]);

    const handlePointerDownCapture = useCallback(() => {
        if (!dismissOnPointerDown) return;
        clearShowTimeout();
        clearHideTimeout();
        setIsVisible(false);
    }, [dismissOnPointerDown, clearShowTimeout, clearHideTimeout]);

    useEffect(() => {
        if (isVisible) {
            // Defer positioning until after tooltip is painted
            const raf = requestAnimationFrame(() => {
                requestAnimationFrame(updatePosition);
            });
            const handleScroll = () => updatePosition();
            const handleResize = () => updatePosition();
            window.addEventListener("scroll", handleScroll, true);
            window.addEventListener("resize", handleResize);
            return () => {
                cancelAnimationFrame(raf);
                window.removeEventListener("scroll", handleScroll, true);
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [isVisible, updatePosition]);

    useEffect(() => {
        return () => {
            clearShowTimeout();
            clearHideTimeout();
        };
    }, [clearShowTimeout, clearHideTimeout]);

    const maxWidthStyle = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setPrefersReducedMotion(mq.matches);
        const handler = () => setPrefersReducedMotion(mq.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    const tooltipContent = isVisible && !disabled && (
        <motion.div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.15, ease: "easeOut" }}
            className={clsx(
                "pointer-events-none fixed z-[9999] rounded-lg border border-neutral-border/20 px-3 py-2 text-sm font-medium shadow-lg",
                "bg-neutral-dark text-white dark:bg-neutral-dark/95 dark:text-neutral-light",
                className,
            )}
            style={{
                top: coords.top,
                left: coords.left,
                maxWidth: maxWidthStyle,
            }}
        >
            {content}
        </motion.div>
    );

    return (
        <>
            <div
                ref={triggerRef}
                className={clsx("inline-flex", triggerClassName)}
                onMouseEnter={handleShow}
                onMouseLeave={handleHide}
                onFocus={showOnFocus ? handleShow : undefined}
                onBlur={showOnFocus ? handleHide : undefined}
                onPointerDownCapture={handlePointerDownCapture}
            >
                {React.isValidElement(children)
                    ? React.cloneElement(children as React.ReactElement<{ "aria-describedby"?: string }>, {
                          "aria-describedby": isVisible ? tooltipId : undefined,
                      } as Record<string, unknown>)
                    : children}
            </div>
            {createPortal(
                <AnimatePresence>{tooltipContent}</AnimatePresence>,
                document.body
            )}
        </>
    );
};
